import axios from "axios";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useEffect, useRef, useMemo } from "react";
import {
  getAdminCompanies,
  getCompanies,
  getEmployees,
  getOffices,
  getPositionOwners,
  getServices,
} from "./address-book";
import {
  invalidateDictionary,
  setDictionaryStore,
} from "../store/dictionary.slice";
import { store } from "../store";
const apiUrl = process.env.REACT_APP_API_URL;

// Dictionary config: attr -> { func, defaultParams }
const dictionaryConfig = {
  services: {
    func: () => getServices,
    defaultParams: [{}, [], 10000, 0, ["id", "name", "isHc", "isMd"]],
  },
  offices: {
    func: () => getOffices,
    defaultParams: [
      {},
      [],
      10000,
      0,
      ["id", "name", "isMd", "isHc", "isFeatured"],
    ],
  },
  companies: {
    func: () => getAdminCompanies,
    defaultParams: [{}, ["name ASC"], 10000, 0, ["id", "name"], false],
  },
  countries: {
    func: () => getCountries,
    defaultParams: [
      { order: ["priority ASC"], fields: ["id", "name", "nationality"] },
    ],
  },
  fieldsOfWorkCategories: {
    func: () => getFieldsOfWorkCategories,
    defaultParams: [{ order: ["priority ASC"], fields: ["id", "name"] }],
  },
  fieldsOfWorks: {
    func: () => getFieldsOfWorks,
    defaultParams: [
      {
        order: ["priority ASC"],
        fields: ["id", "name", "fieldsOfWorkCategoryId"],
      },
    ],
  },
  workTypeMds: {
    func: () => getWorkTypeMds,
    defaultParams: [{ order: ["priority ASC"], fields: ["id", "name"] }],
  },
  languages: {
    func: () => getLanguages,
    defaultParams: [{ order: ["priority ASC"], fields: ["id", "name"] }],
  },
  qualifications: {
    func: () => getQualifications,
    defaultParams: [
      { order: ["priority ASC"], fields: ["id", "name", "level"] },
    ],
  },
  fieldOfStudyCategories: {
    func: () => getFieldsOfStudyCategories,
    defaultParams: [{ order: ["priority ASC"], fields: ["id", "name"] }],
  },
  fieldOfStudies: {
    func: () => getFieldsOfStudies,
    defaultParams: [
      {
        order: ["priority ASC"],
        fields: ["id", "name", "fieldsOfStudyCategoryId"],
      },
    ],
  },
  computerSkillCategories: {
    func: () => getComputerSkillCategories,
    defaultParams: [{ order: ["priority ASC"], fields: ["id", "name"] }],
  },
  computerSkills: {
    func: () => getComputerSkills,
    defaultParams: [
      {
        order: ["priority ASC"],
        fields: ["id", "name", "computerSkillCategoryId"],
      },
    ],
  },
  drivingLicences: {
    func: () => getDrivingLicences,
    defaultParams: [{ order: ["priority ASC"], fields: ["id", "name"] }],
  },
  tags: {
    func: () => getTags,
    defaultParams: [
      {
        include: ["tagCategories"],
        order: ["tagCategoryId ASC", "name ASC"],
        // NOTE: a "tagCategories" reláció az include-ba tartozik, NEM a fields-be —
        // a relációnév a fields tömbben 400 BadRequest-et okoz (LoopBack szigorú
        // szűrő-validáció). Az include így is visszaadja a teljes kategória-objektumot
        // (benne a scope mezővel).
        fields: ["id", "name", "type", "tagCategoryId"],
      },
    ],
  },
  spareTimes: {
    func: () => getSpareTimes,
    defaultParams: [{ order: ["name ASC"], fields: ["id", "name"] }],
  },
  partners: {
    func: () => getPartners,
    defaultParams: [{ order: ["name ASC"], fields: ["id", "name"] }],
  },
  employees: {
    func: () => getEmployees,
    defaultParams: [
      {},
      [],
      10000,
      0,
      ["id", "firstName", "lastName", "userEmployee"],
    ],
  },
  cities: {
    func: () => getCities,
    defaultParams: ["", 100000, { fields: ["id", "name", "zip"] }],
  },
  counties: {
    func: () => getCounties,
    defaultParams: [null, { fields: ["id", "name"] }],
  },
  referers: {
    func: () => getReferers,
    defaultParams: [{ fields: ["id", "name"] }],
  },
};

/**
 * Stale-while-revalidate: a localStorage cache-ből azonnal renderelünk, de ha a
 * mentett adat DICTIONARY_TTL_MS-nál régebbi, a háttérben újratöltjük. Enélkül a
 * cache lejárat nélkül élt, és a böngésző hónapokra beragadt a legelső letöltésnél.
 */
export const DICTIONARY_TTL_MS = 15 * 60 * 1000;

// Sikertelen letöltés után ennyi ideig nem próbálkozunk újra ugyanazzal a
// szótárral, hogy egy halott végpont ne generáljon kérés-áradatot.
const DICTIONARY_RETRY_MS = 30 * 1000;

const inFlight = new Set();
const failedAt = {};

// Stabil referencia: a useDictionary hívói (pl. FilterComputerSkill) spreadelik a
// visszakapott listát, ezért betöltés alatt sem adhatunk vissza undefined-ot.
const EMPTY_DICTIONARY = [];

/**
 * Elavult-e a szótár? Hiányzó vagy időbélyeg nélküli meta esetén igen.
 */
export function isDictionaryStale(meta) {
  if (!meta?.fetchedAt) return true;
  return Date.now() - meta.fetchedAt > DICTIONARY_TTL_MS;
}

/**
 * Fetch a single dictionary and dispatch to Redux store.
 * Non-hook function — can be called from anywhere (hooks, preloader, etc.)
 */
export function fetchDictionary(dispatch, attr, params, parseResponse) {
  const config = dictionaryConfig[attr];
  if (!config) return;
  if (inFlight.has(attr)) return;
  if (failedAt[attr] && Date.now() - failedAt[attr] < DICTIONARY_RETRY_MS)
    return;

  inFlight.add(attr);

  const resolvedParams = params ?? config.defaultParams;
  const func = config.func();

  // A meglévő tartalmat szándékosan nem ürítjük ki a kérés idejére: revalidálás
  // alatt a régi lista marad kint, különben kiürülnének a legördülők.
  func(...(resolvedParams || []))
    .then((response) => {
      if (response.data) {
        const parsedResponse =
          parseResponse && typeof parseResponse === "function"
            ? parseResponse(response.data)
            : response.data;
        delete failedAt[attr];
        dispatch(
          setDictionaryStore({
            attr,
            items: parsedResponse,
          })
        );
      }
    })
    .catch((err) => {
      failedAt[attr] = Date.now();
      console.error(err);
    })
    .finally(() => {
      inFlight.delete(attr);
    });
}

/**
 * Egy vagy több szótár frissességének eldobása. A tartalom megmarad, csak a
 * következő olvasás tölti újra — mutáció (pl. új cég felvétele) után hívandó.
 */
export function invalidateDictionaries(attrs) {
  const list = Array.isArray(attrs) ? attrs : [attrs];
  store.dispatch(invalidateDictionary({ attrs: list }));
}

/**
 * Original single-dictionary hook (kept for backward compatibility).
 * Now uses granular selector — only re-renders when THIS dictionary changes.
 */
export function useDictionary(attr, params, parseResponse) {
  const data = useSelector((state) => state.dictionary.store[attr]);
  const meta = useSelector((state) => state.dictionary.meta[attr]);
  const dispatch = useDispatch();
  const requestedRef = useRef(false);

  useEffect(() => {
    if (requestedRef.current) return;
    if (data && !isDictionaryStale(meta)) return;
    requestedRef.current = true;
    fetchDictionary(dispatch, attr, params, parseResponse);
    // A hívók (pl. FilterComputerSkill) inline objektumot adnak át params-ként,
    // ezért csak az attr-re és a frissességre iratkozunk fel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attr, data, meta, dispatch]);

  return data ?? EMPTY_DICTIONARY;
}

/**
 * Batch dictionary hook — fetches multiple dictionaries and returns them all at once.
 * Only re-renders when any of the requested dictionaries actually changes.
 *
 * @param {string[]} attrs - List of dictionary attribute names to load
 * @param {Object} [overrides] - Per-attr overrides: { attrName: { params, parseResponse } }
 * @returns {{ data: Object, allLoaded: boolean }}
 */
export function useDictionaries(attrs, overrides = {}) {
  const dispatch = useDispatch();
  const fetchedRef = useRef({});

  const { data, meta, allLoaded } = useSelector(
    (state) => {
      const result = {};
      const metaResult = {};
      let loaded = true;
      for (const attr of attrs) {
        const val = state.dictionary.store[attr];
        result[attr] = val;
        metaResult[attr] = state.dictionary.meta[attr];
        if (!val || (Array.isArray(val) && !val.length)) {
          loaded = false;
        }
      }
      return { data: result, meta: metaResult, allLoaded: loaded };
    },
    (a, b) => {
      if (a.allLoaded !== b.allLoaded) return false;
      for (const attr of attrs) {
        if (a.data[attr] !== b.data[attr]) return false;
        if (a.meta[attr] !== b.meta[attr]) return false;
      }
      return true;
    }
  );

  // Hiányzó szótár letöltése, meglévő de elavult revalidálása. Mountonként és
  // szótáranként egyszer — a cache-ből jövő adat közben kint marad, tehát az
  // allLoaded azonnal true, a revalidálás nem blokkolja a rendert.
  useEffect(() => {
    for (const attr of attrs) {
      if (fetchedRef.current[attr]) continue;
      if (data[attr] && !isDictionaryStale(meta[attr])) continue;
      fetchedRef.current[attr] = true;
      const override = overrides[attr] || {};
      fetchDictionary(dispatch, attr, override.params, override.parseResponse);
    }
  });

  return { data, allLoaded };
}

export const isactiveFilter = (filter) => {
  if (!filter) {
    filter = {};
  }
  if (!filter.where) {
    filter.where = {
      isActive: 1,
    };
  } else if (
    filter.where.and &&
    !filter.where.and.find((item) => Object.keys(item) === "isActive")
  ) {
    filter.where.and.push({
      isActive: 1,
    });
  } else if (filter.where) {
    const filterWhere = filter.where;
    filter.where = {
      and: [
        {
          isActive: 1,
        },
        filterWhere,
      ],
    };
  }
  return filter;
};

export const getTags = (filter = {}, withInactives = false) => {
  return axios.get(`${apiUrl}/tags`, {
    params: withInactives ? filter : isactiveFilter(filter),
  });
};
export const getTag = (id) => {
  return axios.get(`${apiUrl}/tags/${id}`, {});
};
export const setTag = (id, data) => {
  if (id) {
    return axios.patch(`${apiUrl}/tags/${id}`, data);
  } else {
    return axios.post(`${apiUrl}/tags`, data);
  }
};
export const deleteTag = (id) => {
  return axios.delete(`${apiUrl}/tags/${id}`, {});
};

export const getFieldsOfWorkCategories = (
  filter = {},
  withInactives = false
) => {
  return axios.get(`${apiUrl}/fields-of-work-categories`, {
    params: withInactives ? filter : isactiveFilter(filter),
  });
};
export const getFieldsOfWorkCategory = (id) => {
  return axios.get(`${apiUrl}/fields-of-work-categories/${id}`, {});
};
export const setFieldsOfWorkCategory = (id, data) => {
  if (id) {
    return axios.patch(`${apiUrl}/fields-of-work-categories/${id}`, data);
  } else {
    return axios.post(`${apiUrl}/fields-of-work-categories`, data);
  }
};
export const deleteFieldsOfWorkCategory = (id) => {
  return axios.delete(`${apiUrl}/fields-of-work-categories/${id}`, {});
};

export const getFieldsOfWorks = (filter = {}, withInactives = false) => {
  return axios.get(`${apiUrl}/fields-of-works`, {
    params: withInactives ? filter : isactiveFilter(filter),
  });
};
export const getFieldsOfWorksByCategory = (
  filter = {},
  withInactives = false
) => {
  return axios.get(`${apiUrl}/fields-of-works`, {
    params: withInactives ? filter : isactiveFilter(filter),
  });
};
export const getFieldsOfWork = (id) => {
  return axios.get(`${apiUrl}/fields-of-works/${id}`, {});
};
export const setFieldsOfWork = (id, data) => {
  if (id) {
    return axios.patch(`${apiUrl}/fields-of-works/${id}`, data);
  } else {
    return axios.post(`${apiUrl}/fields-of-works`, data);
  }
};
export const deleteFieldsOfWork = (id) => {
  return axios.delete(`${apiUrl}/fields-of-works/${id}`, {});
};

export const getQualifications = (filter = {}, withInactives = false) => {
  filter = withInactives ? filter : isactiveFilter(filter);
  const params = { ...filter, ...{ order: "level ASC" } };
  return axios.get(`${apiUrl}/qualifications`, {
    params: params,
  });
};
export const getQualification = (id) => {
  return axios.get(`${apiUrl}/qualifications/${id}`, {});
};
export const setQualification = (id, data) => {
  if (id) {
    return axios.patch(`${apiUrl}/qualifications/${id}`, data);
  } else {
    return axios.post(`${apiUrl}/qualifications`, data);
  }
};
export const deleteQualification = (id) => {
  return axios.delete(`${apiUrl}/qualifications/${id}`, {});
};

export const getFieldsOfStudyCategories = (
  filter = {},
  withInactives = false
) => {
  return axios.get(`${apiUrl}/fields-of-study-categories`, {
    params: withInactives ? filter : isactiveFilter(filter),
  });
};
export const getFieldsOfStudyCategory = (id) => {
  return axios.get(`${apiUrl}/fields-of-study-categories/${id}`, {});
};
export const setFieldsOfStudyCategory = (id, data) => {
  if (id) {
    return axios.patch(`${apiUrl}/fields-of-study-categories/${id}`, data);
  } else {
    return axios.post(`${apiUrl}/fields-of-study-categories`, data);
  }
};
export const deleteFieldsOfStudyCategory = (id) => {
  return axios.delete(`${apiUrl}/fields-of-study-categories/${id}`, {});
};

export const getFieldsOfStudies = (filter = {}, withInactives = false) => {
  return axios.get(`${apiUrl}/fields-of-studies`, {
    params: withInactives ? filter : isactiveFilter(filter),
  });
};
export const getFieldsOfStudiesByCategory = (categoryId) => {
  return axios.get(`${apiUrl}/fields-of-studies`, {
    params: {
      where: {
        fieldsOfStudyCategoryId: categoryId,
      },
    },
  });
};
export const getFieldsOfStudy = (id) => {
  return axios.get(`${apiUrl}/fields-of-studies/${id}`, {});
};
export const setFieldsOfStudy = (id, data) => {
  if (id) {
    return axios.patch(`${apiUrl}/fields-of-studies/${id}`, data);
  } else {
    return axios.post(`${apiUrl}/fields-of-studies`, data);
  }
};
export const deleteFieldsOfStudy = (id) => {
  return axios.delete(`${apiUrl}/fields-of-studies/${id}`, {});
};

export const getWorkTypeMds = (filter = {}, withInactives = false) => {
  return axios.get(`${apiUrl}/work-type-mds`, {
    params: withInactives ? filter : isactiveFilter(filter),
  });
};
export const getWorkTypeMd = (id) => {
  return axios.get(`${apiUrl}/work-type-mds/${id}`, {});
};
export const setWorkTypeMd = (id, data) => {
  if (id) {
    return axios.patch(`${apiUrl}/work-type-mds/${id}`, data);
  } else {
    return axios.post(`${apiUrl}/work-type-mds`, data);
  }
};
export const deleteWorkTypeMd = (id) => {
  return axios.delete(`${apiUrl}/work-type-mds/${id}`, {});
};

export const getComputerSkillCategories = (
  filter = {},
  withInactives = false
) => {
  return axios.get(`${apiUrl}/computer-skill-categories`, {
    params: withInactives ? filter : isactiveFilter(filter),
  });
};
export const getComputerSkillCategory = (id) => {
  return axios.get(`${apiUrl}/computer-skill-categories/${id}`, {});
};
export const setComputerSkillCategory = (id, data) => {
  if (id) {
    return axios.patch(`${apiUrl}/computer-skill-categories/${id}`, data);
  } else {
    return axios.post(`${apiUrl}/computer-skill-categories`, data);
  }
};
export const deleteComputerSkillCategory = (id) => {
  return axios.delete(`${apiUrl}/computer-skill-categories/${id}`, {});
};

export const getComputerSkills = (filter = {}, withInactives = false) => {
  return axios.get(`${apiUrl}/computer-skills`, {
    params: withInactives ? filter : isactiveFilter(filter),
  });
};
export const getComputerSkill = (id) => {
  return axios.get(`${apiUrl}/computer-skills/${id}`, {});
};
export const getComputerSkillsByCategory = (
  categoryId,
  withInactives = false
) => {
  const filter = {
    where: {
      computerSkillCategoryId: categoryId,
    },
  };
  return axios.get(`${apiUrl}/computer-skills`, {
    params: withInactives ? filter : isactiveFilter(filter),
  });
};
export const setComputerSkill = (id, data) => {
  if (id) {
    return axios.patch(`${apiUrl}/computer-skills/${id}`, data);
  } else {
    return axios.post(`${apiUrl}/computer-skills`, data);
  }
};
export const deleteComputerSkill = (id) => {
  return axios.delete(`${apiUrl}/computer-skills/${id}`, {});
};

export const getReferers = (filter = {}, withInactives = false) => {
  return axios.get(`${apiUrl}/referers`, {
    params: withInactives ? filter : isactiveFilter(filter),
  });
};
export const getReferer = (id) => {
  return axios.get(`${apiUrl}/referers/${id}`, {});
};
export const setReferer = (id, data) => {
  if (id) {
    return axios.patch(`${apiUrl}/referers/${id}`, data);
  } else {
    return axios.post(`${apiUrl}/referers`, data);
  }
};
export const deleteReferer = (id) => {
  return axios.delete(`${apiUrl}/referers/${id}`, {});
};

export const getPartners = (filter = {}, withInactives = false) => {
  return axios.get(`${apiUrl}/partners`, {
    params: withInactives ? filter : isactiveFilter(filter),
  });
};
export const getPartner = (id) => {
  return axios.get(`${apiUrl}/partners/${id}`, {});
};
export const setPartner = (id, data) => {
  if (id) {
    return axios.patch(`${apiUrl}/partners/${id}`, data);
  } else {
    return axios.post(`${apiUrl}/partners`, data);
  }
};
export const deletePartner = (id) => {
  return axios.delete(`${apiUrl}/partners/${id}`, {});
};

export const getCountries = (filter = {}, withInactives = false) => {
  return axios.get(`${apiUrl}/countries`, {
    params: withInactives ? filter : isactiveFilter(filter),
  });
};
export const getCountriesAutocomplete = (nationality) => {
  const filter = {};
  if (nationality) {
    filter.where = {
      nationality: { like: `${nationality}%` },
    };
  }
  filter.limit = 25;
  return axios.get(`${apiUrl}/countries`, { params: filter });
};

export const getCountry = (id) => {
  return axios.get(`${apiUrl}/countries/${id}`, {});
};
export const setCountry = (id, data) => {
  if (id) {
    return axios.patch(`${apiUrl}/countries/${id}`, data);
  } else {
    return axios.post(`${apiUrl}/countries`, data);
  }
};
export const deleteCountry = (id) => {
  return axios.delete(`${apiUrl}/countries/${id}`, {});
};

export const getAdminCitiesGroup = (search) => {
  return axios.get(`${apiUrl}/admin/cities-group-id/${search}`, {});
};

export const getDictionaryCities = (filter = {}, withInactives = false) => {
  return axios.get(`${apiUrl}/cities`, {
    params: withInactives ? filter : isactiveFilter(filter),
  });
};
export const getCities = (where, limit, filter) => {
  if (!filter) {
    filter = {
      where: {},
    };
  }
  if (where) {
    filter.where = { ...where, ...{ isActive: true } };
  }
  if (limit) {
    filter.limit = limit;
  }
  return axios.get(`${apiUrl}/cities`, { params: filter });
};
export const getCity = (id) => {
  return axios.get(`${apiUrl}/cities/${id}`, {});
};
export const setCity = (id, data) => {
  if (id) {
    return axios.patch(`${apiUrl}/cities/${id}`, data);
  } else {
    return axios.post(`${apiUrl}/cities`, data);
  }
};
export const deleteCity = (id) => {
  return axios.delete(`${apiUrl}/cities/${id}`, {});
};

export const getLanguages = (filter = {}, withInactives = false) => {
  return axios.get(`${apiUrl}/languages`, {
    params: withInactives ? filter : isactiveFilter(filter),
  });
};
export const getLanguage = (id) => {
  return axios.get(`${apiUrl}/languages/${id}`, {});
};
export const setLanguage = (id, data) => {
  if (id) {
    return axios.patch(`${apiUrl}/languages/${id}`, data);
  } else {
    return axios.post(`${apiUrl}/languages`, data);
  }
};
export const deleteLanguage = (id) => {
  return axios.delete(`${apiUrl}/languages/${id}`, {});
};

export const getDrivingLicences = (filter = {}, withInactives = false) => {
  return axios.get(`${apiUrl}/driving-licences`, {
    params: withInactives ? filter : isactiveFilter(filter),
  });
};
export const getDrivingLicence = (id) => {
  return axios.get(`${apiUrl}/driving-licences/${id}`, {});
};
export const setDrivingLicence = (id, data) => {
  if (id) {
    return axios.patch(`${apiUrl}/driving-licences/${id}`, data);
  } else {
    return axios.post(`${apiUrl}/driving-licences`, data);
  }
};
export const deleteDrivingLicence = (id) => {
  return axios.delete(`${apiUrl}/driving-licences/${id}`, {});
};

export const getSpareTimes = (filter = {}, withInactives = false) => {
  return axios.get(`${apiUrl}/spare-times`, {
    params: withInactives ? filter : isactiveFilter(filter),
  });
};
export const getSpareTime = (id) => {
  return axios.get(`${apiUrl}/spare-times/${id}`, {});
};
export const setSpareTime = (id, data) => {
  if (id) {
    return axios.patch(`${apiUrl}/spare-times/${id}`, data);
  } else {
    return axios.post(`${apiUrl}/spare-times`, data);
  }
};
export const deleteSpareTime = (id) => {
  return axios.delete(`${apiUrl}/spare-times/${id}`, {});
};

export const getCounties = (counties, filter = {}) => {
  if (typeof filter !== "object") {
    filter = {};
  }
  if (counties) {
    filter.where = {
      id: { inq: counties },
      isActive: true,
    };
  } else {
    filter.where = {
      isActive: true,
    };
  }
  return axios.get(`${apiUrl}/counties`, filter);
};
export const getCounty = (id) => {
  return axios.get(`${apiUrl}/counties/${id}`, {});
};
export const setCounty = (id, data) => {
  if (id) {
    return axios.patch(`${apiUrl}/counties/${id}`, data);
  } else {
    return axios.post(`${apiUrl}/counties`, data);
  }
};
export const deleteCounty = (id) => {
  return axios.delete(`${apiUrl}/counties/${id}`, {});
};

export const getDictionaryTagCategories = (
  filter = {},
  withInactives = false
) => {
  return axios.get(`${apiUrl}/tag-categories`, {
    params: withInactives ? filter : isactiveFilter(filter),
  });
};
export const getTagCategories = (categories, filter = {}) => {
  if (categories) {
    filter.where = {
      id: { inq: categories },
      isActive: true,
    };
  } else {
    filter.where = {
      isActive: true,
    };
  }
  return axios.get(`${apiUrl}/tag-categories`, filter);
};
export const getTagCategory = (id) => {
  return axios.get(`${apiUrl}/tag-categories/${id}`, {});
};
export const setTagCategory = (id, data) => {
  if (id) {
    return axios.patch(`${apiUrl}/tag-categories/${id}`, data);
  } else {
    return axios.post(`${apiUrl}/tag-categories`, data);
  }
};
export const deleteTagCategory = (id) => {
  return axios.delete(`${apiUrl}/tag-categories/${id}`, {});
};
